import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Menu, X, PaintBucket } from "lucide-react";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Services", href: "/services" },
    { name: "Portfolio", href: "/portfolio" },
    { name: "Process", href: "/process" },
    { name: "About", href: "/about" },
    { name: "Contact", href: "/contact" },
  ];

  const isActive = (href) => location.pathname.startsWith(href);
  const onHero = isHomePage && !isScrolled;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
            <div
              className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors duration-300 ${
                onHero ? "bg-white/15 backdrop-blur-sm" : "bg-brand-orange"
              }`}
            >
              <PaintBucket className={`w-4.5 h-4.5 ${onHero ? "text-white" : "text-white"}`} />
            </div>
            <span
              className={`font-heading text-lg font-semibold tracking-tight transition-colors duration-300 ${
                onHero ? "text-white" : "text-brand-navy"
              }`}
            >
              Roll Orlando
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-[13px] font-medium tracking-wide uppercase transition-colors duration-300 ${
                  isActive(link.href)
                    ? "text-brand-orange"
                    : onHero
                      ? "text-white/80 hover:text-white"
                      : "text-brand-slate hover:text-brand-navy"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/quote">
              <Button
                data-testid="get-quote-btn"
                className={`rounded-full font-heading text-[13px] font-medium uppercase tracking-wide h-10 px-6 transition-all duration-300 ${
                  onHero
                    ? "bg-white text-brand-navy hover:bg-white/90"
                    : "bg-brand-navy text-white hover:bg-brand-navy/90"
                }`}
              >
                Get a Quote
              </Button>
            </Link>
          </div>

          {/* Mobile menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-btn"
                className={`h-9 w-9 ${onHero ? "text-white hover:bg-white/10" : "text-brand-navy hover:bg-brand-ice"}`}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-white p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-5 border-b border-border/40">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 bg-brand-orange rounded-md flex items-center justify-center">
                      <PaintBucket className="w-4.5 h-4.5 text-white" />
                    </div>
                    <span className="font-heading text-lg font-semibold text-brand-navy">
                      Roll Orlando
                    </span>
                  </div>
                </div>
                <div className="flex flex-col py-4 flex-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.name}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                      className={`px-6 py-3 text-[15px] font-medium tracking-wide transition-colors ${
                        isActive(link.href)
                          ? "text-brand-orange bg-brand-orange/5"
                          : "text-brand-slate hover:text-brand-navy hover:bg-brand-ice/50"
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
                <div className="p-5 border-t border-border/40">
                  <Link to="/quote" onClick={() => setIsOpen(false)}>
                    <Button
                      data-testid="mobile-get-quote-btn"
                      className="w-full rounded-full bg-brand-navy text-white font-heading font-medium h-11"
                    >
                      Get a Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
